import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  MessageSquare, 
  Mail, 
  Send, 
  Bot,
  User,
  Loader2,
  CheckCircle2,
  HelpCircle,
  FileQuestion,
  CreditCard,
  Shield,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { user } = useAuth();
  const [showAlek, setShowAlek] = useState(true);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hi! I'm Alek, your Privacy Assistant. 👋\n\nI'm here to help answer your questions quickly. I can assist with:\n\n• Account & billing questions\n• Privacy protection features\n• Technical support & troubleshooting\n• General privacy inquiries\n\n**How would you like to proceed?**\n\n1️⃣ **Chat with me now** - I'll try to resolve your issue instantly\n2️⃣ **Contact PrivacyShield Support Team** - Fill out a form and get a response within 24 hours\n\nJust type your question or click \"Email Support\" below to reach our team directly!"
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailForm, setEmailForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    subject: '',
    message: ''
  });
  const messagesEndRef = useRef(null);

  const quickActions = [
    { icon: HelpCircle, label: "How does PrivacyShield work?", query: "How does PrivacyShield protect my privacy?" },
    { icon: CreditCard, label: "Billing & Subscription", query: "I have a question about billing or my subscription" },
    { icon: Shield, label: "Premium Features", query: "What premium features do I get with a subscription?" },
    { icon: AlertTriangle, label: "Report an Issue", query: "I want to report a technical issue" },
    { icon: Mail, label: "Email Support", query: "EMAIL_FORM" }
  ];

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (messageText) => {
    if (!messageText.trim()) return;

    // Check for email form trigger
    if (messageText === 'EMAIL_FORM') {
      setShowEmailForm(true);
      setMessages(prev => [...prev, 
        { role: 'user', content: "I'd like to contact support via email" },
        { role: 'assistant', content: "Of course! I'll show you the email form. Please fill in your details and our team will get back to you within 24 hours." }
      ]);
      return;
    }

    const userMessage = { role: 'user', content: messageText };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      // Use the public contact chat API (no auth required)
      const response = await axios.post(`${API}/chat/contact`, {
        message: `You are Alek, the friendly Privacy Assistant for PrivacyShield. You help users with:
- Account and billing questions
- Privacy protection features and how they work
- Technical support and troubleshooting
- General privacy advice

IMPORTANT INSTRUCTIONS:
1. Be helpful, friendly, and concise
2. If the question is simple (FAQs, features, how-to), answer it directly
3. If the question requires account access, billing changes, or technical investigation, politely say:
   "I'd be happy to help, but this requires our support team's assistance. Please click the 'Email Support' button below or describe your issue, and I'll help you send it to our team who will respond within 24 hours."
4. Always end complex answers with: "Does this answer your question, or would you like me to connect you with our support team?"

User question: ${messageText}`,
      });

      const botResponse = response.data.response;
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: botResponse
      }]);
    } catch (error) {
      // Fallback responses if API fails
      const fallbackResponses = {
        billing: "For billing questions, you can view and manage your subscription in your Profile page. If you need a refund or have payment issues, please use the email form below and our team will assist you within 24 hours.",
        premium: "Premium features include:\n\n• Password Breach Checker (real HIBP database)\n• Email Breach Scanner\n• Data Broker Removal Service\n• Identity Monitoring Dashboard\n• Advanced Privacy Guides\n• Unlimited AI Assistant queries\n\nYou can upgrade anytime from the Pricing page!",
        issue: "I'm sorry you're experiencing issues. Could you describe the problem in detail? If it's a technical bug, please include:\n\n1. What you were trying to do\n2. What happened instead\n3. Any error messages\n\nYou can also email our support team for faster resolution.",
        default: "I'd be happy to help with that! Could you provide more details about your question? Or if you prefer, I can connect you with our support team via email."
      };

      let response = fallbackResponses.default;
      const lowerMessage = messageText.toLowerCase();
      if (lowerMessage.includes('bill') || lowerMessage.includes('payment') || lowerMessage.includes('subscription') || lowerMessage.includes('cancel')) {
        response = fallbackResponses.billing;
      } else if (lowerMessage.includes('premium') || lowerMessage.includes('feature')) {
        response = fallbackResponses.premium;
      } else if (lowerMessage.includes('issue') || lowerMessage.includes('bug') || lowerMessage.includes('error') || lowerMessage.includes('not working')) {
        response = fallbackResponses.issue;
      }

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    
    if (!emailForm.name || !emailForm.email || !emailForm.subject || !emailForm.message) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      // In production, this would send an email via SendGrid/Resend
      await axios.post(`${API}/contact`, emailForm);
      toast.success('Message sent! We\'ll get back to you within 24 hours.');
      setShowEmailForm(false);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "Thanks for reaching out! Your message has been sent to our support team. We typically respond within 24 hours. Is there anything else I can help you with in the meantime?"
      }]);
      setEmailForm({ name: user?.name || '', email: user?.email || '', subject: '', message: '' });
    } catch (error) {
      // Store locally if API fails
      toast.success('Message recorded! Our team will review it soon.');
      setShowEmailForm(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] py-8 md:py-16" data-testid="contact-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#00FF94]/10 border border-[#00FF94]/20 mb-4">
            <Bot className="w-8 h-8 text-[#00FF94]" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            Meet <span className="text-[#00FF94]">Alek</span>
          </h1>
          <p className="text-gray-400">
            Your AI Privacy Assistant - available 24/7 to help
          </p>
        </div>

        {/* Chat Interface */}
        <Card className="bg-[#0a0a0a] border-[#262626] overflow-hidden">
          <CardContent className="p-0">
            
            {/* Messages Area */}
            <div className="h-[400px] overflow-y-auto p-4 space-y-4">
              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    msg.role === 'assistant' 
                      ? 'bg-[#00FF94]/10 border border-[#00FF94]/20' 
                      : 'bg-blue-500/10 border border-blue-500/20'
                  }`}>
                    {msg.role === 'assistant' 
                      ? <Bot className="w-4 h-4 text-[#00FF94]" />
                      : <User className="w-4 h-4 text-blue-400" />
                    }
                  </div>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                    msg.role === 'assistant'
                      ? 'bg-[#171717] text-gray-300'
                      : 'bg-[#00FF94] text-black'
                  }`}>
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ))}
              
              {loading && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#00FF94]/10 border border-[#00FF94]/20 flex items-center justify-center">
                    <Bot className="w-4 h-4 text-[#00FF94]" />
                  </div>
                  <div className="bg-[#171717] rounded-2xl px-4 py-3">
                    <Loader2 className="w-4 h-4 text-[#00FF94] animate-spin" />
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Quick Actions */}
            {messages.length <= 2 && !showEmailForm && (
              <div className="px-4 pb-4">
                <p className="text-xs text-gray-500 mb-2">Quick actions:</p>
                <div className="flex flex-wrap gap-2">
                  {quickActions.map((action, index) => (
                    <button
                      key={index}
                      onClick={() => sendMessage(action.query)}
                      className="flex items-center gap-2 px-3 py-2 bg-[#171717] border border-[#262626] rounded-lg text-xs text-gray-400 hover:text-white hover:border-[#00FF94]/30 transition-colors"
                    >
                      <action.icon className="w-3 h-3" />
                      {action.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Email Form */}
            {showEmailForm && (
              <div className="p-4 border-t border-[#262626]">
                <form onSubmit={handleEmailSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      placeholder="Your name"
                      value={emailForm.name}
                      onChange={(e) => setEmailForm({...emailForm, name: e.target.value})}
                      className="bg-[#171717] border-[#262626] text-white"
                    />
                    <Input
                      type="email"
                      placeholder="Your email"
                      value={emailForm.email}
                      onChange={(e) => setEmailForm({...emailForm, email: e.target.value})}
                      className="bg-[#171717] border-[#262626] text-white"
                    />
                  </div>
                  <Input
                    placeholder="Subject"
                    value={emailForm.subject}
                    onChange={(e) => setEmailForm({...emailForm, subject: e.target.value})}
                    className="bg-[#171717] border-[#262626] text-white"
                  />
                  <Textarea
                    placeholder="Your message..."
                    value={emailForm.message}
                    onChange={(e) => setEmailForm({...emailForm, message: e.target.value})}
                    className="bg-[#171717] border-[#262626] text-white min-h-[100px]"
                  />
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowEmailForm(false)}
                      className="border-[#262626] text-gray-400"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="btn-primary flex-1"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                      Send Message
                    </Button>
                  </div>
                </form>
              </div>
            )}

            {/* Input Area */}
            {!showEmailForm && (
              <div className="p-4 border-t border-[#262626]">
                <form 
                  onSubmit={(e) => { e.preventDefault(); sendMessage(input); }}
                  className="flex gap-2"
                >
                  <Input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Ask Alek anything about privacy..."
                    className="flex-1 bg-[#171717] border-[#262626] text-white"
                    disabled={loading}
                  />
                  <Button 
                    type="submit" 
                    disabled={loading || !input.trim()}
                    className="btn-primary"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </form>
              </div>
            )}

          </CardContent>
        </Card>

        {/* Additional Contact Info */}
        <div className="mt-8 grid md:grid-cols-3 gap-4">
          <Card className="bg-[#0a0a0a] border-[#262626]">
            <CardContent className="p-4 text-center">
              <Mail className="w-6 h-6 text-[#00FF94] mx-auto mb-2" />
              <p className="text-sm text-white font-medium">Email Support</p>
              <p className="text-xs text-gray-500">support@privacyshield.store</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0a0a0a] border-[#262626]">
            <CardContent className="p-4 text-center">
              <MessageSquare className="w-6 h-6 text-[#00FF94] mx-auto mb-2" />
              <p className="text-sm text-white font-medium">Live Chat</p>
              <p className="text-xs text-gray-500">Talk to Alek 24/7</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0a0a0a] border-[#262626]">
            <CardContent className="p-4 text-center">
              <CheckCircle2 className="w-6 h-6 text-[#00FF94] mx-auto mb-2" />
              <p className="text-sm text-white font-medium">Response Time</p>
              <p className="text-xs text-gray-500">Within 24 hours</p>
            </CardContent>
          </Card>
        </div>

      </div>
    </div>
  );
}
